import { createBrowserAuthClient } from './auth-runtime.mjs';
import { ExternalSignInError } from './external-sign-in.js';

export interface VerifiedBrowserActor { readonly id: string; readonly displayName: string; readonly email: string }
export interface AuthGateway {
  getActor(): Promise<VerifiedBrowserActor | null>;
  signIn(input: { email: string; password: string }): Promise<void>;
  signUp(input: { name: string; email: string; password: string; handle?: string; postingTimezone?: string }): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  resetPassword(input: { token: string; newPassword: string }): Promise<void>;
  resendVerification(email: string): Promise<void>;
  /** Resolves once the browser has been sent to Google; rejects only with ExternalSignInError. */
  signInWithGoogle(): Promise<void>;
  /** The same start, for linking Google to the account that is signed in. */
  linkGoogle(): Promise<void>;
  signOut(): Promise<void>;
}

export interface BrowserAuthGatewayOptions {
  readonly runtime?: ReturnType<typeof createBrowserAuthClient>;
  /** Top-level navigation; replaced in tests. */
  readonly navigate?: (url: string) => void;
}

export class AuthGatewayError extends Error {
  constructor(readonly code: string, message: string) { super(message); this.name = 'AuthGatewayError'; }
}

const googleAuthorizationOrigin = 'https://accounts.google.com';

/**
 * Reads the SDK result for a Google start (sign-in or linking). Unlike `check`
 * below, this never copies a message out of the result: only the code, the
 * HTTP status and the parsed origin of the returned URL decide the outcome.
 */
function googleAuthorizationUrl(result: unknown, linking: boolean): string {
  const field = (value: unknown, key: string): unknown =>
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined;
  const error = field(result, 'error');
  if (error) {
    const status = field(error, 'status'), code = field(error, 'code');
    if (code === 'PROVIDER_NOT_FOUND' || code === 'GOOGLE_UNAVAILABLE' || status === 404) throw new ExternalSignInError('unavailable');
    if (status === 429) throw new ExternalSignInError('rateLimited');
    // Linking needs a session that signed in a moment ago; the server refuses an older or missing one.
    if (linking && (code === 'SESSION_NOT_FRESH' || status === 401)) throw new ExternalSignInError('signInAgain');
    throw new ExternalSignInError('startFailed');
  }
  const url = field(field(result, 'data'), 'url');
  if (typeof url !== 'string') throw new ExternalSignInError('startFailed');
  let destination: URL;
  try { destination = new URL(url); } catch { throw new ExternalSignInError('startFailed'); }
  // The origin is scheme, host and port together; an address that carries credentials is not one Google hands out.
  if (destination.origin !== googleAuthorizationOrigin || destination.username !== '' || destination.password !== '') throw new ExternalSignInError('startFailed');
  // Navigate to what was validated, not to the unparsed text.
  return destination.href;
}

/** Better Auth owns cookies, credential transport, password handling and verification. */
export function createBrowserAuthGateway(options: BrowserAuthGatewayOptions = {}): AuthGateway {
  const auth = options.runtime ?? createBrowserAuthClient();
  const navigate = options.navigate ?? ((url: string) => { window.location.assign(url); });
  const object = (value: unknown): Record<string, unknown> => {
    if (typeof value !== 'object' || value === null) throw new Error('Authentication returned an invalid response.');
    return value as Record<string, unknown>;
  };
  const check = (result: unknown): Record<string, unknown> => {
    const value = object(result);
    if (value['error']) {
      const error = object(value['error']);
      throw new AuthGatewayError(typeof error['code'] === 'string' && /^[A-Z0-9_]{1,80}$/.test(error['code']) ? error['code'] : 'AUTH_FAILED',
        typeof error['message'] === 'string' ? error['message'] : 'Authentication could not complete.');
    }
    return value;
  };
  /** The one place that leaves for the provider: only after the answer names the Google authorization origin. */
  const startGoogle = async (request: () => Promise<unknown>, linking: boolean): Promise<void> => {
    let result: unknown;
    // A transport failure carries library text; it is dropped here.
    try { result = await request(); } catch { throw new ExternalSignInError('startFailed'); }
    const destination = googleAuthorizationUrl(result, linking);
    try { navigate(destination); } catch { throw new ExternalSignInError('startFailed'); }
  };
  return {
    async getActor() {
      const result = check(await auth.getSession());
      if (!result['data']) return null;
      const user = object(object(result['data'])['user']);
      if (user['emailVerified'] !== true) return null;
      if (typeof user['id'] !== 'string' || typeof user['name'] !== 'string' || typeof user['email'] !== 'string') throw new Error('Authentication returned an invalid user.');
      return { id: user['id'], displayName: user['name'], email: user['email'] };
    },
    async signIn(input) { check(await auth.signIn(input)); },
    async signUp(input) { check(await auth.signUp(input)); },
    async requestPasswordReset(email) { check(await auth.requestPasswordReset(email)); },
    async resetPassword(input) { check(await auth.resetPassword(input)); },
    async resendVerification(email) { check(await auth.resendVerification(email)); },
    signInWithGoogle: () => startGoogle(() => auth.signInWithGoogle(), false),
    linkGoogle: () => startGoogle(() => auth.linkGoogle(), true),
    async signOut() { check(await auth.signOut()); },
  };
}
